import {
  Box,
  Drawer,
  Typography,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar,
  CircularProgress,
  Dialog,
} from "@mui/material";
import {
  fetchPost,
  fetchGet,
  formatStat,
  getStringHash,
} from "../util/helpers";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconMail,
  IconExternalLink,
  IconMoodSmile,
  IconMoodSad,
  IconMoodEmpty,
  IconTrendingUp,
  IconUsers,
  IconEye,
  IconPhoto,
  IconX,
  IconSparkles,
  IconRefresh,
  IconAnalyze,
} from "@tabler/icons-react";
import { memo, useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components";

const platformToIcon = {
  youtube: <IconBrandYoutube size="18" />,
  tiktok: <IconBrandTiktok size="18" />,
  facebook: <IconBrandFacebook size="18" />,
  instagram: <IconBrandInstagram size="18" />,
};

const platformColors = {
  youtube: "#FF0000",
  tiktok: "#000000",
  facebook: "#1877F2",
  instagram: "#E4405F",
};

const SENTIMENT_ICONS = {
  positive: { icon: IconMoodSmile, color: "var(--icon-color-green)" },
  neutral: { icon: IconMoodEmpty, color: "var(--icon-color-yellow)" },
  negative: { icon: IconMoodSad, color: "#EF4444" },
};

const StatCard = ({ icon, label, value, subtitle }) => (
  <Box
    sx={{
      flex: "1 1 calc(33.33% - 8px)",
      background: "var(--neutral-50)",
      borderRadius: "12px",
      padding: "16px 12px",
      textAlign: "center",
      border: "1px solid var(--neutral-200)",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "var(--secondary-300)",
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(159, 37, 246, 0.08)",
      },
    }}
  >
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ mb: 0.5, color: "var(--secondary-500)" }}
    >
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: "var(--body-text-color-500)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "0.65rem",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: "var(--main-text-color)", mt: 0.25 }}
    >
      {value}
    </Typography>
    {subtitle && (
      <Typography
        variant="caption"
        sx={{ color: "var(--body-text-color-200)", fontSize: "0.7rem" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const SentimentBar = ({ label, value, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--body-text-color-600)" }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color }}>
        {value}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={value}
      sx={{
        height: 6,
        borderRadius: 3,
        backgroundColor: "var(--neutral-100)",
        "& .MuiLinearProgress-bar": {
          borderRadius: 3,
          background: color,
        },
      }}
    />
  </Box>
);

function SentimentSection({ creatorId }) {
  const [sentimentState, setSentimentState] = useState("loading");
  const [sentimentData, setSentimentData] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollForResults = useCallback(
    (runId) => {
      stopPolling();
      setSentimentState("polling");

      pollRef.current = setInterval(async () => {
        try {
          const res = await fetchGet(`/brand/sentiment/run/${runId}`);
          if (res.status === "SUCCEEDED" && res.data) {
            stopPolling();
            setSentimentData(res.data);
            setSentimentState("completed");
            setIsStale(false);
          } else if (res.status === "FAILED") {
            stopPolling();
            setSentimentState("error");
            setError("Analysis failed. Please try again later.");
          }
        } catch {
          stopPolling();
          setSentimentState("error");
          setError("Failed to check analysis status.");
        }
      }, 5000);
    },
    [stopPolling]
  );

  const fetchSentiment = useCallback(async () => {
    if (!creatorId) return;
    setSentimentState("loading");
    setError(null);
    try {
      const res = await fetchGet(`/brand/creators/${creatorId}/sentiment`);
      if (res.status === "completed" && res.data && res.data.sampleSize > 0) {
        setSentimentData(res.data);
        setSentimentState("completed");
        setIsStale(!!res.stale);
      } else if (res.status === "pending" && res.runId) {
        pollForResults(res.runId);
      } else {
        setSentimentState("empty");
      }
    } catch {
      setSentimentState("empty");
    }
  }, [creatorId, pollForResults]);

  useEffect(() => {
    fetchSentiment();
    return stopPolling;
  }, [fetchSentiment, stopPolling]);

  const handleAnalyze = async () => {
    setSentimentState("polling");
    setError(null);
    try {
      const res = await fetchPost(`/brand/creators/${creatorId}/sentiment`);
      if (res.runId) {
        pollForResults(res.runId);
      } else if (res.status === "pending" && res.runId) {
        pollForResults(res.runId);
      } else {
        setError("Unable to start analysis.");
        setSentimentState("error");
      }
    } catch (err) {
      const errMsg =
        err?.json ? (await err.json())?.error : "Failed to start analysis.";
      setError(errMsg || "Failed to start analysis.");
      setSentimentState("error");
    }
  };

  const overallConfig = sentimentData
    ? SENTIMENT_ICONS[sentimentData.overall] || SENTIMENT_ICONS.neutral
    : SENTIMENT_ICONS.neutral;
  const OverallIcon = overallConfig.icon;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <IconMoodSmile size={16} color="var(--secondary-500)" />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
        >
          Sentiment Analysis
        </Typography>
        <Chip
          label="Beta"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 700,
            background: "var(--color-gradient-light)",
            color: "var(--secondary-600)",
            border: "1px solid var(--secondary-100)",
          }}
        />
      </Box>
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid var(--neutral-200)",
          p: 2.5,
        }}
      >
        {sentimentState === "loading" && (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress size={24} sx={{ color: "var(--secondary-500)" }} />
          </Box>
        )}

        {sentimentState === "empty" && (
          <Box textAlign="center" py={2}>
            <IconAnalyze size={32} color="var(--body-text-color-200)" style={{ marginBottom: 8 }} />
            <Typography
              variant="body2"
              sx={{ color: "var(--body-text-color-500)", mb: 2 }}
            >
              Analyze this creator's social media comments to understand audience sentiment.
            </Typography>
            <Button
              gradient
              style={{ fontWeight: 600, borderRadius: 10, padding: "8px 24px" }}
              onClick={handleAnalyze}
            >
              Analyze Sentiment
            </Button>
          </Box>
        )}

        {sentimentState === "polling" && (
          <Box textAlign="center" py={3}>
            <CircularProgress size={28} sx={{ color: "var(--secondary-500)", mb: 1.5 }} />
            <Typography
              variant="body2"
              sx={{ color: "var(--body-text-color-600)", fontWeight: 600 }}
            >
              Analyzing comments...
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "var(--body-text-color-200)", display: "block", mt: 0.5 }}
            >
              This may take a few minutes
            </Typography>
          </Box>
        )}

        {sentimentState === "error" && (
          <Box textAlign="center" py={2}>
            <Typography
              variant="body2"
              sx={{ color: "#EF4444", mb: 1.5 }}
            >
              {error || "Something went wrong."}
            </Typography>
            <Button
              style={{ fontWeight: 600, borderRadius: 10, padding: "8px 24px" }}
              onClick={handleAnalyze}
            >
              Try Again
            </Button>
          </Box>
        )}

        {sentimentState === "completed" && sentimentData && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: `linear-gradient(135deg, ${overallConfig.color}26, ${overallConfig.color}0d)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <OverallIcon size={20} color={overallConfig.color} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: overallConfig.color,
                      textTransform: "capitalize",
                    }}
                  >
                    {sentimentData.overall}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--body-text-color-200)", fontSize: "0.65rem" }}
                  >
                    Based on {sentimentData.sampleSize.toLocaleString()} comments
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: overallConfig.color }}
                >
                  {sentimentData.positive}%
                </Typography>
                {isStale && (
                  <Tooltip title="Data is over 2 weeks old. Click to refresh." arrow>
                    <IconButton
                      size="small"
                      onClick={handleAnalyze}
                      sx={{ color: "var(--body-text-color-200)" }}
                    >
                      <IconRefresh size={16} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: "var(--neutral-100)" }} />

            <SentimentBar
              label="Positive"
              value={sentimentData.positive}
              color="linear-gradient(90deg, #4AD991, #22C55E)"
            />
            <SentimentBar
              label="Neutral"
              value={sentimentData.neutral}
              color="linear-gradient(90deg, #FEC53D, #F59E0B)"
            />
            <SentimentBar
              label="Negative"
              value={sentimentData.negative}
              color="linear-gradient(90deg, #F87171, #EF4444)"
            />

            {sentimentData.topKeywords?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "var(--body-text-color-500)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontSize: "0.6rem",
                  }}
                >
                  Top Keywords
                </Typography>
                <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.75}>
                  {sentimentData.topKeywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        background: "var(--neutral-50)",
                        border: "1px solid var(--neutral-200)",
                        color: "var(--body-text-color-600)",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

function ApplicantDrawer({ open, onClose, creator, setCreator, campaignId }) {
  const [previewImage, setPreviewImage] = useState(null);

  if (!creator) return null;

  const handleConfirmPartnership = async () => {
    if (creator.response && creator.response.status === "matched") {
      return;
    }
    await fetchPost(`/brand/campaigns/${campaignId}/matches`, {
      creatorUserDocId: creator.userDocId,
      responseId: creator.response.id,
    });
    setCreator((creator) => {
      creator.response.status = "matched";
      return creator;
    });
    onClose();
  };

  const isMatched = creator.response?.status === "matched";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{
        sx: {
          background: "#FAFAFE",
          borderLeft: "1px solid var(--neutral-200)",
        },
      }}
    >
      <Box sx={{ minWidth: 340, width: "32vw", maxWidth: 520 }}>
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "linear-gradient(135deg, rgba(159,37,246,0.04) 0%, rgba(92,164,251,0.04) 100%)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid var(--neutral-200)",
            px: 3,
            py: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <IconSparkles size={18} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  background: "var(--color-gradient)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.3px",
                }}
              >
                CREATOR INSIGHTS
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ color: "var(--body-text-color-500)" }}>
              <IconX size={18} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          {/* Profile Card */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "16px",
              border: "1px solid var(--neutral-200)",
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <Box display="flex" gap={2} alignItems="center">
              <Avatar
                src={
                  creator?.images?.length > 0
                    ? creator?.images[0]
                    : `../gradient${getStringHash(creator?.name, 10)}.png`
                }
                alt={creator.name}
                sx={{
                  width: 64,
                  height: 64,
                  border: "3px solid var(--neutral-100)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              />
              <Box flex={1}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "var(--main-text-color)", lineHeight: 1.3 }}
                >
                  {creator?.name}
                </Typography>
                <Box display="flex" gap={0.75} mt={1} flexWrap="wrap">
                  {creator?.stats?.channels?.map((channel) => (
                    <Tooltip key={channel.platform} title={`View ${channel.platform}`} arrow>
                      <Box
                        onClick={() => {
                          if (channel.url) window.open(channel.url, "_blank");
                        }}
                        sx={{
                          cursor: channel.url ? "pointer" : "default",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          background: "var(--neutral-50)",
                          border: "1px solid var(--neutral-200)",
                          color: platformColors[channel.platform] || "var(--main-text-color)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background: "var(--neutral-100)",
                            transform: channel.url ? "scale(1.08)" : "none",
                          },
                        }}
                      >
                        {platformToIcon[channel.platform]}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>
            {creator?.description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid var(--neutral-100)",
                  color: "var(--body-text-color-600)",
                  lineHeight: 1.6,
                }}
              >
                {creator.description}
              </Typography>
            )}
          </Box>

          {/* Key Metrics */}
          <Box
            display="flex"
            gap="8px"
            flexWrap="wrap"
            sx={{ mb: 2.5 }}
          >
            <StatCard
              icon={<IconUsers size={18} />}
              label="Followers"
              value={formatStat(creator?.stats?.totalFollowers)}
            />
            <StatCard
              icon={<IconEye size={18} />}
              label="Reach"
              value={formatStat(creator?.stats?.totalReach)}
            />
            <StatCard
              icon={<IconTrendingUp size={18} />}
              label="Posts"
              value={formatStat(creator?.stats?.totalPosts)}
            />
          </Box>

          {/* Content Preview */}
          <Box sx={{ mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <IconPhoto size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
              >
                Content Preview
              </Typography>
            </Box>
            <Box
              display="flex"
              gap={1}
              flexWrap="wrap"
              justifyContent="flex-start"
              sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--neutral-200)",
                p: 1.5,
              }}
            >
              {creator?.images?.length > 0 ? (
                creator.images.map((img, index) => (
                  <Box
                    key={index}
                    onClick={() => setPreviewImage(img)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPreviewImage(img);
                      }
                    }}
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      cursor: "pointer",
                      transition: "transform 0.2s ease",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                  >
                    <img
                      src={img}
                      loading="lazy"
                      alt={creator?.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "var(--body-text-color-200)", p: 2, width: "100%", textAlign: "center" }}
                >
                  No content available
                </Typography>
              )}
            </Box>
          </Box>

          {/* Sentiment Analysis */}
          <SentimentSection creatorId={creator.userDocId} />

          {/* Partnership Action */}
          <Box
            sx={{
              background: isMatched
                ? "linear-gradient(135deg, rgba(74,217,145,0.08), rgba(74,217,145,0.02))"
                : "linear-gradient(135deg, rgba(159,37,246,0.06), rgba(92,164,251,0.04))",
              borderRadius: "12px",
              border: `1px solid ${isMatched ? "rgba(74,217,145,0.2)" : "var(--secondary-100)"}`,
              p: 2.5,
              mb: 2.5,
              textAlign: "center",
            }}
          >
            <Button
              disabled={isMatched}
              gradient={!isMatched}
              style={{
                fontWeight: 700,
                borderRadius: 10,
                width: "100%",
                padding: "10px 24px",
                fontSize: "0.875rem",
              }}
              onClick={handleConfirmPartnership}
            >
              {isMatched ? "Partnership Confirmed" : "Confirm Partnership"}
            </Button>
            {isMatched && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "var(--icon-color-green)", fontWeight: 600 }}
              >
                This creator has been matched to your campaign
              </Typography>
            )}
          </Box>

          {/* Contact Section */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "12px",
              border: "1px solid var(--neutral-200)",
              p: 2.5,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <IconMail size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "var(--main-text-color)", letterSpacing: "0.2px" }}
              >
                Send a Custom Email
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "var(--body-text-color-500)", mb: 2, lineHeight: 1.6 }}
            >
              Reach out to clarify campaign details, negotiate terms, or discuss creative direction.
            </Typography>
            <Button
              style={{ borderRadius: 10, width: "100%" }}
              href={`mailto:${creator.email}?subject=Gigl: New Campaign Partnership Opportunity`}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <IconExternalLink size={14} />
                Open Email Client
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0,0,0,0.7)" },
        }}
      >
        {previewImage && (
          <>
            <IconButton
              onClick={() => setPreviewImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                zIndex: 1,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
              size="small"
            >
              <IconX size={18} />
            </IconButton>
            <img
              src={previewImage}
              alt="Content preview"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
              }}
            />
          </>
        )}
      </Dialog>
    </Drawer>
  );
}

export default memo(ApplicantDrawer);
